import React, { useEffect, useRef, useState } from 'react';
import { IoMusicalNotes, IoChevronDown, IoChevronUp, IoExpand, IoOptions } from 'react-icons/io5';
import { useLanguage } from '../i18n/LanguageContext';
import { useLyricsPosition } from '../hooks/useLyricsPosition';

const OFFSET_STEP_SEC = 0.1;
const OFFSET_MIN_SEC = -15;
const OFFSET_MAX_SEC = 15;

// Compact collapsible lyrics section for VoiceRoomPanel, same open/close
// pattern as .TranscriptPanel there. onExpand opens the fullscreen karaoke
// mode (LyricsOverlay) - this component owns none of that overlay's own
// state, just the button that requests it. offsetSec/setOffsetSec come from
// VoiceRoomPanel's single shared useLyricsSyncOffset() call (see
// useLyricsPosition.js) - NOT owned here, so an adjustment made in this
// panel is immediately visible to LyricsOverlay too, not just on its next
// remount.
//
// alwaysExpanded: the phone voice sheet gives lyrics a whole tab of their
// own, so there's nothing to collapse - the header keeps its title and
// sync/fullscreen buttons but stops being a toggle.
export default function LyricsPanel({ musicState, onExpand, offsetSec, setOffsetSec, alwaysExpanded = false }) {
  const { t } = useLanguage();
  const [expandedState, setExpanded] = useState(false);
  const expanded = alwaysExpanded || expandedState;
  const [showSyncControl, setShowSyncControl] = useState(false);
  const { lines, lyricsStatus, plainText, activeIndex } = useLyricsPosition(musicState, offsetSec);
  const linesRef = useRef(null);

  // Keeps the active line roughly centered in the scroll area as it
  // advances - same "auto-follow, don't fight a user who scrolled away"
  // shape as VoiceRoomPanel's own transcriptListRef effect.
  useEffect(() => {
    if (!expanded || activeIndex < 0 || !linesRef.current) return;
    const el = linesRef.current.querySelector(`[data-line-index="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [expanded, activeIndex]);

  if (!lyricsStatus) return null; // no track playing / lyrics feature not applicable right now

  return (
    <div className="LyricsPanel">
      <div className="LyricsPanelHeader">
        <button className="LyricsPanelToggle" onClick={() => setExpanded((v) => !v)} disabled={alwaysExpanded}>
          {!alwaysExpanded && (expanded ? <IoChevronUp /> : <IoChevronDown />)}
          <IoMusicalNotes />
          {t('lyrics.panelTitle')}
        </button>
        {expanded && lyricsStatus === 'ready' && (
          <div className="LyricsPanelActions">
            <button className="LyricsSyncButton" onClick={() => setShowSyncControl((v) => !v)} title={t('lyrics.syncAdjust')}>
              <IoOptions />
            </button>
            <button className="LyricsExpandButton" onClick={onExpand} title={t('lyrics.expand')}>
              <IoExpand />
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <>
          {showSyncControl && lyricsStatus === 'ready' && (
            <div className="LyricsSyncControl">
              <span className="descGray">{t('lyrics.syncAdjust')}</span>
              <input
                type="range"
                min={OFFSET_MIN_SEC}
                max={OFFSET_MAX_SEC}
                step={OFFSET_STEP_SEC}
                value={offsetSec}
                onChange={(e) => setOffsetSec(parseFloat(e.target.value))}
              />
              <span className="LyricsSyncValue">{offsetSec.toFixed(1)}s</span>
              {/* Phone sheet only (alwaysExpanded): a finger can't land a
                  30s-wide slider on a 0.1s step - nudge buttons and a reset
                  instead. Rounded so repeated nudges don't drift into
                  0.30000000000000004. */}
              {alwaysExpanded && (
                <div className="LyricsSyncNudge">
                  <button type="button" onClick={() => setOffsetSec(Math.max(OFFSET_MIN_SEC, Math.round((offsetSec - OFFSET_STEP_SEC) * 10) / 10))}>-0.1</button>
                  <button type="button" onClick={() => setOffsetSec(0)} disabled={offsetSec === 0}>0</button>
                  <button type="button" onClick={() => setOffsetSec(Math.min(OFFSET_MAX_SEC, Math.round((offsetSec + OFFSET_STEP_SEC) * 10) / 10))}>+0.1</button>
                </div>
              )}
            </div>
          )}

          {lyricsStatus === 'pending' && <div className="descGray LyricsEmpty">{t('lyrics.loading')}</div>}
          {lyricsStatus === 'notFound' && <div className="descGray LyricsEmpty">{t('lyrics.notFound')}</div>}
          {lyricsStatus === 'failed' && <div className="descGray LyricsEmpty">{t('lyrics.failed')}</div>}

          {lyricsStatus === 'plainOnly' && (
            <div className="LyricsPlainText">{plainText}</div>
          )}

          {lyricsStatus === 'ready' && lines && (
            <div className="LyricsLines" ref={linesRef}>
              {lines.map((line, i) => (
                <div
                  key={i}
                  data-line-index={i}
                  className={`LyricsLine ${i === activeIndex ? 'LyricsLineActive' : ''}`}
                >
                  {line.text}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
